import { SecondaryButton } from './SecondaryButton';
import { Strings } from '../strings';

/**
 * design-system/06-Components/07-Overlays.md §4a — the one way back out of a screen you navigated into.
 * Mirrors android/…/Components/AnchoredBack.kt and ios/MysliKrok/Components/AnchoredBack.swift.
 *
 * Anchored above the bottom navigation, in the same place and the same shape as the anchored prompt that
 * asks whether to leave child mode. Before this the product had three ways back: a full-width „Späť"
 * button at the top of Ocenenia, a small „Prehľad" text link above the title of a skill screen, and this
 * (R0124). Three answers to one question, none of them wrong on its own.
 *
 * **It does not name where it goes**, though a breadcrumb was reaching for that. „Späť na Prehľad" reads
 * well and „Späť na Miško" does not — Slovak declines the noun, and assembling a user-facing sentence from
 * a template and a name is exactly what BR-13 forbids. „Späť" is one word, needs no case, and already
 * exists on all three clients.
 *
 * Anchoring rather than rendering inline: a control at the top of the content moves with the content, so
 * it scrolls away on a long screen and sits under a reader's thumb on none of them. Fixed above the
 * navigation, it is in the same place on every screen and reachable without scrolling back.
 *
 * The page behind does not move — the same reason `AnchoredPrompt` is anchored (R0099).
 */
export function AnchoredBack({
  onBack,
  isChildMode = false,
}: {
  onBack: () => void;
  /** Larger target, per §4. */
  isChildMode?: boolean;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        // Clear of the navigation, which stays visible and in place underneath — §4.
        bottom: `calc(var(--touch-parent) + var(--space-4) + var(--space-4))`,
        display: 'flex',
        justifyContent: 'center',
        padding: '0 var(--space-4)',
        pointerEvents: 'none',
        zIndex: 20,
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          width: '100%',
          maxWidth: 'var(--column)',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-l)',
          boxShadow: '0 6px 24px rgba(15, 23, 42, 0.18)',
          padding: isChildMode ? 'var(--space-5)' : 'var(--space-4)',
        }}
      >
        <SecondaryButton
          label={Strings.actionBack}
          onClick={onBack}
          height={isChildMode ? 'var(--touch-child)' : 'var(--touch-parent)'}
        />
      </div>
    </div>
  );
}
